import React, { useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { CryptodraftContext } from '../../../../contexts/card/cryptodraft/context';
import Glossary from './Glossary';
import ModalRoot, { Modal, ModalBody, ModalHeader } from '../../../../components/Modal';
import {
  useLedgerFilteredCurrencies,
  useLedgerFilteredWallets,
} from '../../../../utilities/sharedHooks/ledgerWallets';
import { WALLETS_ALL } from '../../../Accounts/queries';
import {
  SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  USER_PREFERENCES,
} from '../../../../utilities/sharedQueries';
import { filterUsableWallets } from '../../../../utilities/helpers';
import {
  cryptodraftAllowedCurrencies,
  forceResetOptions,
  forceUpdateOptions,
  SETTLMENT_INTREST_PAYMENT_AVAILABLE_CURRENCIES,
} from '../constants';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import { useCardStatus, useUserPersonalInfo } from '../../../../utilities/sharedHooks/user';
import { cardProviders } from '../../constants';
import { BANK_REQUESTS_COINS } from '../../../Accounts/constants';
import { CARD_CRYPTODRAFT, CARD_CRYPTODRAFT_SETTLEMENT } from '../../queries';
import { GenericLoading } from '../../../../components/Placeholders';
import { GET_ACTIVE_STAKING_AMOUNT_WITH_TIERS } from '../../../Staking/queries';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';

const MainLayout = ({ children = null, className = '' }) => {
  const {
    state,
    openScreen,
    closeTerms,
    updateCryptodraftAllowedWallets,
    updateSelectedWallet,
    updateSelectedSettlementWallet,
    updateCryptodraftSettings,
    updateUser,
    updateFiatCurrency,
    updatePrincipalCurrency,
    updateCryptodraftDetails,
    updateCryptodraftSettelementDetails,
    updateCryptodraftAllowedSettlementsWallets,
    updateIsExtendCryptodraft,
    updateCryptodraftTierLTV,
    forceUpdateAction,
    forceResetAction,
  } = useContext(CryptodraftContext);

  const history = useHistory();

  const { state: locationState } = useLocation();

  const { data: wallets, refetch: refetchWallets } = useLedgerFilteredWallets(WALLETS_ALL);
  const { data: ledgerFilteredCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  const {
    data: cryptodraftDetails,
    refetch: refetchCryptodraftDetails,
    isLoading: isLoadingCryptodraftDetails,
  } = useQuery(CARD_CRYPTODRAFT, { enabled: false });

  const { data: activeStakingAmountTiers, refetch: refetchActiveStakingAmountTiers } = useQuery(
    GET_ACTIVE_STAKING_AMOUNT_WITH_TIERS,
    {
      enabled: false,
    }
  );

  const { data: cryptodraftSettlementDetails, refetch: refetchCryptodraftSettlementDetails } =
    useQuery(CARD_CRYPTODRAFT_SETTLEMENT, { enabled: false });

  const forceUpdate = state?.forceUpdate;
  const forceReset = state?.forceReset;

  const { data: userPref } = useQuery(USER_PREFERENCES);
  const { cardProvider, isUS } = useUserPersonalInfo();
  const { data: cardStatus } = useCardStatus();

  const fiatCurrency = useMemo(() => {
    if (cardProvider === cardProviders.MONAVATE && cardStatus?.displayCurrency) {
      return cardStatus?.displayCurrency;
    }
    return userPref?.displayCurrency;
  }, [cardProvider, cardStatus, userPref?.displayCurrency]);

  const principalCurrency = useMemo(() => {
    if (fiatCurrency) {
      if (fiatCurrency.toLowerCase() === BANK_REQUESTS_COINS.EUR) {
        return BANK_REQUESTS_COINS.EUROC;
      }
      if (
        fiatCurrency.toLowerCase() === BANK_REQUESTS_COINS.USD ||
        (fiatCurrency?.toLowerCase() === BANK_REQUESTS_COINS.GBP &&
          cardProvider === cardProviders.MONAVATE)
      ) {
        return BANK_REQUESTS_COINS.USDT;
      }
      return fiatCurrency;
    }

    return '';
  }, [fiatCurrency, cardProvider]);

  const useableWallets = useMemo(() => {
    if (wallets) {
      return filterUsableWallets(wallets);
    }
    return [];
  }, [wallets]);

  const allowedWallets = useMemo(() => {
    if (useableWallets && ledgerFilteredCurrencies) {
      return useableWallets.filter(
        (w) =>
          cryptodraftAllowedCurrencies.includes(w?.coin) &&
          ledgerFilteredCurrencies[w?.coin]?.allow_as_cryptodraft_collateral
      );
    }
    return [];
  }, [useableWallets, ledgerFilteredCurrencies]);

  const allowedSettlementsWallets = useMemo(
    () =>
      useableWallets.filter((w) =>
        SETTLMENT_INTREST_PAYMENT_AVAILABLE_CURRENCIES.includes(w.coin.toUpperCase())
      ),
    [useableWallets]
  );

  const initialDepositAmountSettingsKeys = useMemo(() => {
    if (allowedWallets) {
      const minimumKeys = allowedWallets.map(
        (item) => `cryptodraft_minimum_initial_deposit_amount_${item.coin.toUpperCase()}`
      );
      const maximumKeys = allowedWallets.map(
        (item) => `cryptodraft_maximum_initial_deposit_amount_${item.coin.toUpperCase()}`
      );
      return [...minimumKeys, ...maximumKeys];
    }
    return [];
  }, [allowedWallets]);

  const [settings] = useSetting('crypto_draft_options', [
    'cryptodraft_ltv_ranges',
    'cryptodraft_interest_mpr',
    'cryptodraft_us_ltv_ranges',
    'cryptodraft_us_interest_mpr',
    ...initialDepositAmountSettingsKeys,
  ]);

  useEffect(() => {
    refetchCryptodraftDetails();
    refetchActiveStakingAmountTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!locationState?.initScreen) {
      history.push('/dashboard/card/details');
    } else if (locationState?.openExtendFromAccountScreen) {
      updateIsExtendCryptodraft();
      openScreen(locationState?.initScreen, { openedFromAccountScreen: true });
    } else {
      openScreen(locationState?.initScreen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (allowedWallets) {
      updateCryptodraftAllowedWallets(allowedWallets);
      if (allowedWallets.length > 0) {
        if (cryptodraftDetails) {
          updateSelectedWallet(
            allowedWallets.find((wallet) => wallet.coin === cryptodraftDetails?.collateral_currency)
          );
        } else {
          updateSelectedWallet(
            allowedWallets.find((wallet) => wallet.coin === cryptodraftAllowedCurrencies[0])
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedWallets, cryptodraftDetails]);

  useEffect(() => {
    if (allowedSettlementsWallets && cryptodraftDetails) {
      updateCryptodraftAllowedSettlementsWallets(allowedSettlementsWallets);
      if (allowedSettlementsWallets.length > 0) {
        updateSelectedSettlementWallet(
          allowedSettlementsWallets.find(
            (item) => item.coin === cryptodraftDetails?.collateral_currency
          )
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedSettlementsWallets, cryptodraftDetails]);

  useEffect(() => {
    if (
      settings &&
      state?.cryptodraftSettings === null &&
      initialDepositAmountSettingsKeys?.length > 0
    ) {
      updateCryptodraftSettings(settings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, state?.cryptodraftSettings, initialDepositAmountSettingsKeys]);

  useEffect(() => {
    if (cryptodraftDetails) {
      updateCryptodraftDetails(cryptodraftDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cryptodraftDetails]);

  useEffect(() => {
    if (cryptodraftSettlementDetails) {
      updateCryptodraftSettelementDetails(cryptodraftSettlementDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cryptodraftSettlementDetails]);

  useEffect(() => {
    if (activeStakingAmountTiers && activeStakingAmountTiers?.userStakingTier) {
      updateCryptodraftTierLTV(
        activeStakingAmountTiers?.userStakingTier?.cryptodraft_ltv_interest_free
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStakingAmountTiers]);

  useEffect(() => {
    // FOR ONLY FORCE UPDATE WALLETS WHEN ITS NEEDED
    if (forceUpdate && forceUpdate[forceUpdateOptions.WALLETS]) {
      forceUpdateAction(forceUpdateOptions.WALLETS, false);
      refetchWallets();
    }
    // FOR ONLY FORCE UPDATE CRYPTODRAFT DETAILS WHEN ITS NEEDED
    if (forceUpdate && forceUpdate[forceUpdateOptions.DETAILS]) {
      forceUpdateAction(forceUpdateOptions.DETAILS, false);
      refetchCryptodraftDetails();
    }
    // FOR ONLY FORCE UPDATE CRYPTODRAFT SETTELEMENT DETAILS WHEN ITS NEEDED
    if (forceUpdate && forceUpdate[forceUpdateOptions.SETTLEMENT_DETAILS]) {
      forceUpdateAction(forceUpdateOptions.SETTLEMENT_DETAILS, false);
      refetchCryptodraftSettlementDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceUpdate]);

  useEffect(() => {
    if (forceReset && forceReset[forceResetOptions.SETTLEMENT_MAIN_WALLET]) {
      if (allowedSettlementsWallets.length > 0) {
        updateSelectedSettlementWallet(
          allowedSettlementsWallets.find(
            (item) => item.coin === cryptodraftDetails.collateral_currency
          )
        );
      }
      forceResetAction(forceResetOptions.SETTLEMENT_MAIN_WALLET, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceReset]);

  useEffect(() => {
    if (fiatCurrency && principalCurrency) {
      updateFiatCurrency(fiatCurrency);
      updatePrincipalCurrency(principalCurrency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiatCurrency, principalCurrency]);

  useEffect(() => {
    updateUser({ cardProvider, isUS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardProvider, isUS]);

  const renderedComponent = useMemo(() => {
    if (isLoadingCryptodraftDetails || !settings) {
      return <GenericLoading isFullScreen={false} />;
    }
    if (Array.isArray(children)) {
      return children?.filter((child) => child.props.keyName === state?.currentScreen);
    }
    return [children]?.filter((child) => child.props.keyName === state?.currentScreen);
  }, [isLoadingCryptodraftDetails, settings, children, state?.currentScreen]);

  const closeCryptodraft = () => history.push('/dashboard/card/details');

  return (
    <div className="d-flex flex-row main-layout">
      <div
        className="d-none d-md-block position-absolute"
        onClick={closeCryptodraft}
        onKeyDown={closeCryptodraft}
        role="button"
        style={{ top: '4%', right: '3%', zIndex: 100 }}
        tabIndex={-1}>
        <i
          className="close-btn cursor-pointer icon icon-close-other text-dark text-f4"
          aria-label="Close"
        />
      </div>
      <div className="d-flex flex-column flex-fill align-items-center h-100vh position-relative">
        <div
          className={`d-flex flex-column w-100 position-relative h-100 px-3 px-md-0 cryptodraft-main-layout ${className}`}
          style={{
            maxWidth: '33.5rem',
            transitionProperty: 'margin',
            transitionDuration: '300ms',
          }}>
          {renderedComponent}
        </div>
      </div>
      <div
        style={{ zIndex: 101 }}
        className={`d-none d-md-block cryptodraft-sidebar bg-lighter ${
          state.termsOpened ? 'py-3 px-4 cryptodraft-sidebar-expanded' : ''
        }`}>
        <div className="d-flex flex-row ps-4 pt-4">
          <i
            onClick={closeTerms}
            onKeyDown={closeTerms}
            role="button"
            className="close-btn cursor-pointer icon icon-close-other text-dark text-f4"
            tabIndex={-1}
            aria-label="Close"
          />
        </div>
        <Glossary />
      </div>
      {state.termsOpened && (
        <ModalRoot rootClassName="d-md-none">
          <Modal specialMobileContentClass="w-auto rounded-0 rounded-top py-3 px-0">
            <ModalHeader>
              <i
                onClick={closeTerms}
                onKeyDown={closeTerms}
                role="button"
                className="close-btn cursor-pointer icon icon-close-other text-shadow"
                tabIndex={-1}
                aria-label="Close"
              />
            </ModalHeader>
            <ModalBody>
              <ModalBodyLayout>
                <Glossary />
              </ModalBodyLayout>
            </ModalBody>
          </Modal>
        </ModalRoot>
      )}
    </div>
  );
};

export default MainLayout;

MainLayout.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  className: PropTypes.string,
};
